import PropTypes from 'prop-types';
import React, { useState } from 'react';

import config from '../config';
import { uploadToDriveNext } from './driveNextUpload';
import Upload from './Upload';

const AttachFile = ({
  accept,
  buttonLabel,
  children,
  disabled,
  driveNext,
  id,
  label,
  multiple,
  optional,
  onChange,
  onError,
  onUploadingChange,
  variant,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  // Tells the consumer how many files are in flight, so it can show progress where the attachments will land.
  const setUploading = (count) => {
    setIsLoading(count > 0);
    onUploadingChange(count);
  };
  const handleUpload = async (files) => {
    if (driveNext) {
      setUploading(files.length);
      const results = await Promise.all(files.map((file) => uploadToDriveNext(file)));
      setUploading(0);
      const errors = results.filter((result) => result.error).map((result) => result.error);
      const uploaded = results.filter((result) => !result.error);
      // onChange first: the consumer treats it as a fresh attempt and clears any prior error, which
      // would otherwise wipe the message set here before it is ever painted.
      if (uploaded.length) onChange(uploaded);
      if (errors.length) onError(errors.join(' '));
      return;
    }
    setUploading(files.length);
    const uploadedFiles = (await Promise.all(files.map((file) => uploadFile(file)))).map((res) => res[0]);
    setUploading(0);
    onChange(uploadedFiles);
  };

  const uploadFile = async (file) => {
    const data = new FormData();
    data.append('uploadFile', file, file.name);

    const options = {
      method: 'POST',
      body: data,
      accept: 'application/json',
    };
    return fetch(`${config.DRIVE_URL}/upload`, options).then((res) => res.json());
  };

  return (
    <Upload
      accept={accept}
      buttonLabel={buttonLabel}
      label={label}
      multiple={multiple}
      disabled={disabled || isLoading}
      id={id}
      optional={optional}
      onUpload={handleUpload}
      variant={variant}>
      {children}
    </Upload>
  );
};

AttachFile.propTypes = {
  accept: PropTypes.string,
  buttonLabel: PropTypes.string,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  /**
   * Upload to drive-next (S3, PROCEDURES feature) instead of legacy drive's GridFS. The result
   * objects then carry `storageKey` and `origin` rather than a GridFS `_id`.
   */
  driveNext: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.string,
  multiple: PropTypes.bool,
  onChange: PropTypes.func,
  onError: PropTypes.func,
  /** Called with the number of files currently uploading: once when they start and with 0 when they finish. */
  onUploadingChange: PropTypes.func,
  optional: PropTypes.bool,
  variant: PropTypes.oneOf(['dropzone', 'icon']),
};

AttachFile.defaultProps = {
  accept: 'image/*,.csv,.xls,.xlsx,.doc,.docx,.pdf,.txt',
  buttonLabel: 'Upload File',
  children: null,
  disabled: false,
  driveNext: false,
  id: '',
  label: 'Drag and drop some files here, or click to select files',
  multiple: true,
  optional: false,
  onChange: () => null,
  onError: () => null,
  onUploadingChange: () => null,
  variant: 'dropzone',
};

export default AttachFile;
